import { Vec3 } from './vec3.js';
import { Ball, BallId } from './ball.js';
import {
  AU,
  BallMode,
  FOLLOW_MODES,
  ORBITAL_PRECESSION,
  TWO_PI,
  HALF_PI,
  WARP_FACTOR_TO_ACCELERATION,
  WARP_FACTOR_TO_AU_PER_SECOND,
  WARP_FACTOR_TO_DECELERATION,
} from './constants.js';

export type DestinyEvent =
  | { type: 'modeChange'; ballId: BallId; mode: BallMode; stamp: number }
  | { type: 'activatingWarp'; ballId: BallId; stamp: number }
  | { type: 'deactivatingWarp'; ballId: BallId; stamp: number }
  | { type: 'exitWarp'; ballId: BallId; stamp: number }
  | { type: 'collision'; ballId: BallId; otherId: BallId; stamp: number };

export interface AddBallParams {
  id: BallId;
  mass?: number;
  radius?: number;
  maxVel?: number;
  isFree?: boolean;
  isGlobal?: boolean;
  isMassive?: boolean;
  isInteractive?: boolean;
  isSpaceJunk?: boolean;
  x?: number;
  y?: number;
  z?: number;
  vx?: number;
  vy?: number;
  vz?: number;
  agility?: number;
  speedFraction?: number;
}

const SIGNUM = (x: number) => (x >= 0.0 ? 1.0 : -1.0);

/** Round to 7 decimal digits, exactly like Destiny's cross-platform "shitround". */
const round7 = (x: number) => Number(BigInt(Math.trunc(x * 10000000))) / 10000000;

/** Destiny's Modulo2pi: shift b into the representation nearest to a. */
function modulo2pi(ab: { a: number; b: number }): void {
  let { a, b } = ab;
  b = b % TWO_PI;
  a = a % TWO_PI;
  if (Math.abs(b - a) < Math.abs(b + TWO_PI - a)) {
    if (Math.abs(b - a) >= Math.abs(b - TWO_PI - a)) b = b - TWO_PI;
  } else {
    if (Math.abs(b + TWO_PI - a) < Math.abs(b - TWO_PI - a)) b = b + TWO_PI;
    else b = b - TWO_PI;
  }
  ab.a = a;
  ab.b = b;
}

/**
 * TypeScript port of Carbon Destiny's Ballpark: one per solar system.
 * Deterministic 1 Hz damped-ODE simulation; identical code extrapolates
 * on the client between server stamps. Ported from
 * github.com/carbonengine/destiny (MIT) with EVE-default settings
 * (old-style orbit, no dynamical orientation, no iterative collision).
 */
export class Ballpark {
  tickInterval = 1000; // ms
  dt = 1.0; // seconds
  friction = 1000000.0;
  currentTime = 0; // tick counter
  time = 0; // absolute ms timestamp of last evolve
  isMaster = true; // server-authoritative park dispatches missile owner checks
  solarSystemId = -1;

  balls = new Map<BallId, Ball>();
  freeBalls = new Map<BallId, Ball>();
  formations: Vec3[][] = [];

  // Roll dynamics constants (Ballpark constructor defaults)
  rollSpeedAcceleration = -3.0;
  rollSpeedDecay = -0.9;
  rollAcceleration = 1.0;
  rollDecay = -0.5;

  onEvent: ((ev: DestinyEvent) => void) | null = null;

  private emit(ev: DestinyEvent): void {
    if (this.onEvent) this.onEvent(ev);
  }

  // ------------------------------------------------------------------
  // Ball management
  // ------------------------------------------------------------------

  addBall(p: AddBallParams): Ball {
    const ball = new Ball(p.id);
    ball.mass = p.mass ?? 0;
    ball.radius = Math.fround(p.radius ?? 0);
    ball.maxVel = Math.fround(p.maxVel ?? 0);
    ball.isGlobal = p.isGlobal ?? false;
    ball.isMassive = p.isMassive ?? false;
    ball.isInteractive = p.isInteractive ?? false;
    ball.isSpaceJunk = p.isSpaceJunk ?? false;
    ball.agility = Math.fround(p.agility ?? 1.0);
    ball.speedFraction = Math.fround(p.speedFraction ?? 1.0);
    ball.newPos.set(p.x ?? 0, p.y ?? 0, p.z ?? 0);
    ball.oldPos.copy(ball.newPos);
    ball.newVel.set(p.vx ?? 0, p.vy ?? 0, p.vz ?? 0);
    ball.oldVel.copy(ball.newVel);
    this.setBallTimeFactor(ball);
    this.balls.set(ball.id, ball);
    if (p.isFree) this.setBallFree(ball.id, true);
    return ball;
  }

  getBall(id: BallId): Ball | undefined {
    return this.balls.get(id);
  }

  removeBall(id: BallId): void {
    const ball = this.balls.get(id);
    if (!ball) return;
    this.stopAllFollowers(ball);
    if (FOLLOW_MODES.includes(ball.mode)) this.stop(ball);
    this.balls.delete(id);
    this.freeBalls.delete(id);
  }

  setBallFree(id: BallId, free: boolean): void {
    const ball = this.balls.get(id);
    if (!ball) return;
    ball.isFree = free;
    if (free) this.freeBalls.set(id, ball);
    else this.freeBalls.delete(id);
  }

  setBallTimeFactor(ball: Ball): void {
    const mass = ball.mass * ball.agility;
    ball.timeFactor = mass <= 0.0 ? 0.0 : Math.exp((-this.friction * this.dt) / mass);
  }

  setBallMass(id: BallId, mass: number): void {
    const ball = this.balls.get(id);
    if (!ball) return;
    ball.mass = mass;
    this.setBallTimeFactor(ball);
  }

  setBallAgility(id: BallId, agility: number): void {
    const ball = this.balls.get(id);
    if (!ball) return;
    ball.agility = Math.fround(agility);
    this.setBallTimeFactor(ball);
  }

  setBallRadius(id: BallId, radius: number): void {
    const ball = this.balls.get(id);
    if (ball) ball.radius = Math.fround(radius);
  }

  setBallMaxVel(id: BallId, maxVel: number): void {
    const ball = this.balls.get(id);
    if (ball) ball.maxVel = Math.fround(maxVel);
  }

  setSpeedFraction(id: BallId, fraction: number): void {
    const ball = this.balls.get(id);
    if (ball) ball.speedFraction = Math.fround(fraction);
  }

  setBallMassive(id: BallId, massive: boolean): void {
    const ball = this.balls.get(id);
    if (ball) ball.isMassive = massive;
  }

  setBallPosition(id: BallId, x: number, y: number, z: number): void {
    const ball = this.balls.get(id);
    if (!ball) return;
    ball.newPos.set(x, y, z);
    ball.oldPos.set(x, y, z);
  }

  setBallVelocity(id: BallId, vx: number, vy: number, vz: number): void {
    const ball = this.balls.get(id);
    if (!ball) return;
    ball.newVel.set(vx, vy, vz);
    ball.oldVel.set(vx, vy, vz);
  }

  loadFormations(formations: [number, number, number][][]): void {
    this.formations = formations.map((slots) => slots.map(([x, y, z]) => new Vec3(x, y, z)));
  }

  setBallFormation(id: BallId, formationId: number): void {
    const ball = this.balls.get(id);
    if (ball) ball.formationId = formationId;
  }

  private setMode(ball: Ball, mode: BallMode): void {
    ball.mode = mode;
    this.emit({ type: 'modeChange', ballId: ball.id, mode, stamp: this.currentTime });
  }

  // ------------------------------------------------------------------
  // Movement commands
  // ------------------------------------------------------------------

  stop(target: BallId | Ball): void {
    const ball = typeof target === 'number' ? this.balls.get(target) : target;
    if (!ball) return;
    if (typeof target === 'number' && ball.mode === BallMode.STOP) return;

    if (FOLLOW_MODES.includes(ball.mode) && ball.followPtr) {
      ball.followPtr.followers.delete(ball.id);
      if (ball.mode === BallMode.FORMATION) ball.followPtr.freeFormationSlot(ball.effectStamp);
      ball.effectStamp = 0;
      ball.followPtr = null;
      ball.followId = 0;
      ball.ownerId = 0;
      ball.followRange = 0.0;
    }
    this.setMode(ball, BallMode.STOP);
  }

  stopAllFollowers(ball: Ball): void {
    for (const fid of [...ball.followers]) {
      const follower = this.balls.get(fid);
      if (follower) this.stop(follower);
    }
    ball.followers.clear();
  }

  gotoPoint(id: BallId, x: number, y: number, z: number): void {
    const ball = this.balls.get(id);
    if (!ball || !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
    this.stop(ball);
    ball.goto.set(x, y, z);
    this.setMode(ball, BallMode.GOTO);
  }

  gotoDirection(id: BallId, x: number, y: number, z: number): void {
    const ball = this.balls.get(id);
    if (!ball || !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
    const dir = new Vec3(x, y, z).normalize();
    const dst = ball.newPos.clone().add(dir.scale(1.0e17));
    this.stop(ball);
    ball.goto.copy(dst);
    this.setMode(ball, BallMode.GOTO);
  }

  followBall(srcId: BallId, dstId: BallId, range: number): void {
    if (!Number.isFinite(range) || srcId === dstId) return;
    const ball = this.balls.get(srcId);
    const dest = this.balls.get(dstId);
    if (!ball || !dest || dest.isMoribund || dest.isCloaked) return;
    this.stop(ball);
    ball.followId = dstId;
    ball.followPtr = dest;
    ball.followRange = Math.fround(range);
    this.setMode(ball, BallMode.FOLLOW);
    dest.followers.add(ball.id);
  }

  orbit(srcId: BallId, dstId: BallId, range: number): void {
    if (!Number.isFinite(range) || srcId === dstId) return;
    const ball = this.balls.get(srcId);
    const dest = this.balls.get(dstId);
    if (!ball || !dest || dest.isMoribund || dest.isCloaked) return;
    this.stop(ball);
    ball.followId = dstId;
    ball.followPtr = dest;
    ball.followRange = Math.fround(range);
    this.setMode(ball, BallMode.ORBIT);
    dest.followers.add(ball.id);
  }

  formationFollow(srcId: BallId, leaderId: BallId, slot: number): void {
    if (srcId === leaderId) return;
    const ball = this.balls.get(srcId);
    const leader = this.balls.get(leaderId);
    if (!ball || !leader || leader.isMoribund) return;
    this.stop(ball);
    ball.followId = leaderId;
    ball.followPtr = leader;
    ball.effectStamp = slot;
    leader.formationSlots[slot] = srcId;
    this.setMode(ball, BallMode.FORMATION);
    leader.followers.add(ball.id);
  }

  warpTo(srcId: BallId, x: number, y: number, z: number, minRange = 0, warpFactor = 1): void {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
    if (minRange < 0.0) minRange = 0.0;
    if (warpFactor <= 0) warpFactor = 1;
    const ball = this.balls.get(srcId);
    if (!ball) return;

    const dst = new Vec3(x, y, z);
    const delta = ball.newPos.clone().sub(dst);
    if (delta.lengthSq() < 10000000000.0) {
      // Too short for warp; do a goto instead
      this.gotoPoint(srcId, x, y, z);
      this.emit({ type: 'exitWarp', ballId: ball.id, stamp: this.currentTime });
      return;
    }
    if (ball.isWarpish() && dst.x === ball.goto.x && dst.y === ball.goto.y && dst.z === ball.goto.z) {
      return; // redundant; keep the align counter
    }
    this.stop(ball);
    ball.goto.copy(dst);
    ball.effectStamp = -1;
    ball.followRange = 0.0;
    ball.warpMinRange = minRange;
    ball.ownerId = warpFactor;
    this.setMode(ball, BallMode.WARP);
  }

  launchMissile(srcId: BallId, dstId: BallId, ownerId: BallId, aimed: boolean, massive: boolean): void {
    const launcher = this.balls.get(Math.abs(ownerId));
    if (!launcher) return;
    const target = dstId !== ownerId ? this.balls.get(dstId) : undefined;

    const ps = launcher.newPos.clone();
    let v0: Vec3;
    let maxVelocity = 0.0;

    if (target && aimed && !target.isCloaked) {
      v0 = target.newPos.clone().sub(ps).normalize();
    } else {
      const vs = launcher.newVel.clone();
      maxVelocity = launcher.maxVel;
      let direction = vs.clone().normalize();
      if (direction.lengthSq() === 0.0) {
        const rotated = launcher.getRotatedVector({ x: 0, y: 0, z: -1 });
        direction = new Vec3(rotated.x, rotated.y, rotated.z);
      }
      v0 = vs.add(direction.scale(Math.max(150.0, maxVelocity)));
    }

    if (massive) this.setBallMassive(srcId, true);
    this.setBallPosition(srcId, ps.x, ps.y, ps.z);
    this.setBallVelocity(srcId, v0.x, v0.y, v0.z);

    if (target && target.isCloaked) return;
    if (!target) {
      if (maxVelocity > 0.0) this.gotoDirection(srcId, v0.x, v0.y, v0.z);
      return;
    }
    this.missileFollow(srcId, dstId, ownerId);
  }

  missileFollow(srcId: BallId, dstId: BallId, ownerId: BallId): void {
    if (srcId === dstId || srcId === ownerId || dstId === ownerId) return;
    const missile = this.balls.get(srcId);
    const target = this.balls.get(dstId);
    if (!missile || !target || target.isMoribund || target.isCloaked) return;
    this.stop(missile);
    missile.followId = dstId;
    missile.followPtr = target;
    // Negative range makes the missile aim for the target's center, not surface
    missile.followRange = Math.fround(-Math.fround(missile.radius + target.radius));
    missile.ownerId = ownerId;
    missile.effectStamp = this.currentTime;
    missile.goto.copy(missile.newVel.clone().normalize().scale(1.0e16));
    missile.speedFraction = 1.0;
    this.setMode(missile, BallMode.MISSILE);
    target.followers.add(missile.id);
  }

  // ------------------------------------------------------------------
  // The integrator: closed-form damped ODE ("solid through viscous media")
  // ------------------------------------------------------------------

  /** Advance (p, v) under acceleration a for time t. Mutates p and v. */
  integrate(p: Vec3, v: Vec3, a: Vec3, m: number, k: number, timeFactor: number, t: number): void {
    if (t === 0.0) return;
    if (t !== this.dt) timeFactor = Math.exp((-k / m) * t);

    const k2 = k * k;
    const ook2 = 1 / k2;
    const ook = 1 / k;

    if (k < 1e-10 * m * t) {
      // Taylor series expansion for tiny friction
      p.x = (m * (a.x * (-k * t) + k * (a.x * t + ((v.x * k) / m) * t)) + p.x * k2) * ook2;
      p.y = (m * (a.y * (-k * t) + k * (a.y * t + ((v.y * k) / m) * t)) + p.y * k2) * ook2;
      p.z = (m * (a.z * (-k * t) + k * (a.z * t + ((v.z * k) / m) * t)) + p.z * k2) * ook2;
    } else {
      p.x = (m * (m * a.x * (timeFactor - 1.0) + k * (a.x * t + v.x - timeFactor * v.x)) + p.x * k2) * ook2;
      p.y = (m * (m * a.y * (timeFactor - 1.0) + k * (a.y * t + v.y - timeFactor * v.y)) + p.y * k2) * ook2;
      p.z = (m * (m * a.z * (timeFactor - 1.0) + k * (a.z * t + v.z - timeFactor * v.z)) + p.z * k2) * ook2;
    }
    v.x = (m * a.x - (m * a.x - v.x * k) * timeFactor) * ook;
    v.y = (m * a.y - (m * a.y - v.y * k) * timeFactor) * ook;
    v.z = (m * a.z - (m * a.z - v.z * k) * timeFactor) * ook;
  }

  // ------------------------------------------------------------------
  // Mode evolvers (accelerations)
  // ------------------------------------------------------------------

  private gotoThrust(ball: Ball, target: Vec3, missile = false): Vec3 {
    const a = target.clone().sub(ball.newPos);
    const length2 = a.lengthSq();
    const dist = ball.speedFraction * ball.maxVel * this.dt;
    const maxThrust = (this.friction * ball.speedFraction * ball.maxVel) / (ball.mass * ball.agility);

    if (!missile && length2 < dist * dist) {
      // Within homing distance: scale thrust down quartically with distance
      a.normalize();
      const coff = length2 / (dist * dist);
      a.scale(maxThrust * coff * coff);
    } else {
      a.normalize();
      a.scale(maxThrust);
    }
    return a;
  }

  private evolveGoto(ball: Ball): Vec3 {
    return this.gotoThrust(ball, ball.goto);
  }

  private evolveWarp(ball: Ball): Vec3 {
    if (ball.isWarping()) return new Vec3();
    if (ball.isAlignedForWarp()) {
      this.emit({ type: 'activatingWarp', ballId: ball.id, stamp: this.currentTime });
      this.realWarp(ball);
      return new Vec3();
    }
    ball.effectStamp--; // "increment" the align tick counter (negative while aligning)
    return this.evolveGoto(ball);
  }

  private evolveMissile(ball: Ball): Vec3 {
    if ((this.currentTime - ball.effectStamp) * this.tickInterval <= 800) {
      // Initial thrust straight along the launch ray
      return this.gotoThrust(ball, ball.goto);
    }
    const other = ball.followPtr!;
    const otherPos = other.newPos;
    const delta = ball.newPos.clone().sub(otherPos);
    const dist = delta.length();
    const cT = dist / ball.maxVel;
    let target = otherPos.clone().add(other.newVel.clone().scale(cT));
    const r = ball.followRange + ball.radius + other.radius;

    if (dist === 0.0) {
      target = otherPos.clone().add(new Vec3(1, 0, 0).scale(r));
    } else {
      target = target.add(delta.scale(r / dist));
    }
    ball.goto.copy(target);
    return this.gotoThrust(ball, target, ball.mode === BallMode.MISSILE);
  }

  private evolveFormation(ball: Ball): Vec3 {
    const zero = new Vec3();
    const other = ball.followPtr!;
    if (other.formationId < 0 || other.formationId > this.formations.length - 1) return zero;
    const slot = ball.effectStamp;
    const formation = this.formations[other.formationId];
    if (slot > formation.length - 1) return zero;

    const offset = formation[slot];
    let length = offset.length();
    if (length === 0.0) length = 1.0;

    const otherPos = other.newPos.clone();
    const scaled = offset.clone().scale((ball.radius + other.radius + length) / length);
    // Rotation happens in the leader's float32 orientation frame
    const rotated = other.getRotatedVector({ x: scaled.x, y: scaled.y, z: scaled.z });
    otherPos.add(new Vec3(rotated.x, rotated.y, rotated.z));

    ball.goto.copy(otherPos);
    return this.gotoThrust(ball, otherPos);
  }

  private evolveFollow(ball: Ball): Vec3 {
    const other = ball.followPtr!;
    const otherPos = other.newPos;
    const delta = ball.newPos.clone().sub(otherPos);
    const dist = delta.length();
    const r = ball.followRange + ball.radius + other.radius;

    let target: Vec3;
    if (dist === 0.0) {
      target = otherPos.clone().add(new Vec3(1, 0, 0).scale(r));
    } else {
      target = otherPos.clone().add(delta.scale(r / dist));
    }
    ball.goto.copy(target);
    return this.gotoThrust(ball, target, ball.mode === BallMode.MISSILE);
  }

  /** Old-style orbit: EVE's default. Precessing orbital plane seeded by ball id. */
  private evolveOrbit(ball: Ball, currentTime: number): Vec3 {
    const cruiseVelocity = ball.speedFraction * ball.maxVel;
    const k = this.friction;
    const maxThrust = (k * cruiseVelocity) / (ball.mass * ball.agility);

    const other = ball.followPtr!;
    const otherPos = other.newPos;
    const r = ball.followRange + ball.radius + other.radius;

    let toVector = otherPos.clone().sub(ball.newPos);
    const dist = toVector.length();
    toVector.normalize();

    let phi1 = currentTime * ORBITAL_PRECESSION;
    const phi2 = (Number(BigInt(ball.id) & 0xffffn)) + currentTime * ORBITAL_PRECESSION;
    const radialVector = new Vec3(
      Math.cos(phi1) * Math.cos(phi2),
      Math.sin(phi2),
      Math.sin(phi1) * Math.cos(phi2),
    );
    // Round to 7 digits for cross-platform determinism (as the original does)
    radialVector.x = round7(radialVector.x);
    radialVector.y = round7(radialVector.y);
    radialVector.z = round7(radialVector.z);

    radialVector.cross(toVector);
    radialVector.normalize();

    // Choose a goto point tangent to the orbit sphere
    const toComp = dist * dist - r * r;
    if (toComp >= 0.0) {
      const radComp = (r * Math.sqrt(toComp)) / dist;
      toVector = toVector.scale(toComp / dist).add(radialVector.clone().scale(radComp));
      toVector.normalize();
    }

    let radialFactor = Math.exp((-(r - dist) * (r - dist)) / 40000.0);
    radialFactor = round7(radialFactor);

    phi1 = -toVector.dot(radialVector);

    let transverseFactor = 1.0 + radialFactor * radialFactor * (phi1 * phi1 - 1.0);
    if (transverseFactor > 0.0) {
      transverseFactor = radialFactor * phi1 + Math.sqrt(transverseFactor);
    } else {
      transverseFactor = radialFactor * phi1;
    }
    transverseFactor *= SIGNUM(dist - r);

    const a = radialVector
      .clone()
      .scale(radialFactor)
      .add(toVector.clone().scale(transverseFactor))
      .scale(maxThrust);
    ball.goto.copy(ball.newPos.clone().add(a.clone().scale(10.0 * AU)));
    return a;
  }

  private evolveStop(ball: Ball): void {
    // Legacy damping hack on the y component only (faithful to the original)
    ball.newVel.y = (ball.newVel.y - 0.07 * ball.newVel.y) * 0.9345794392523364485981308411215;
  }

  private evolveBehaviorForBall(ball: Ball): void {
    ball.lastG.set(0, 0, 0);
    ball.lastC.set(0, 0, 0);
    ball.collisions.length = 0;

    let a = new Vec3();
    switch (ball.mode) {
      case BallMode.WARP:
        a = this.evolveWarp(ball);
        break;
      case BallMode.GOTO:
        a = this.evolveGoto(ball);
        break;
      case BallMode.MISSILE:
        a = this.evolveMissile(ball);
        break;
      case BallMode.FORMATION:
        a = this.evolveFormation(ball);
        break;
      case BallMode.FOLLOW:
        a = this.evolveFollow(ball);
        break;
      case BallMode.ORBIT:
        a = this.evolveOrbit(ball, this.currentTime);
        break;
      case BallMode.STOP:
        this.evolveStop(ball);
        break;
      default:
        break;
    }
    // CapAcceleration is a no-op in the original (early return)
    ball.lastG.copy(a);
  }

  // ------------------------------------------------------------------
  // Warp
  // ------------------------------------------------------------------

  private realWarp(ball: Ball): void {
    this.stopAllFollowers(ball);
    let dst = ball.goto.clone();
    const delta = ball.newPos.clone().sub(dst).normalize();
    dst = dst.add(delta.scale(ball.warpMinRange));
    const total = dst.clone().sub(ball.newPos);

    ball.goto.copy(dst);
    ball.effectStamp = this.currentTime;
    ball.lastCollision = total.length(); // repurposed: total warp length
    ball.isMassive = false;
  }

  private setupWarpConstants(warpFactor: number, warpDistance: number) {
    let warpSpeed = warpFactor * WARP_FACTOR_TO_AU_PER_SECOND * AU;
    const accelRate = warpFactor * WARP_FACTOR_TO_ACCELERATION;
    const decelRate = Math.min(warpFactor * WARP_FACTOR_TO_DECELERATION, 2.0);

    warpSpeed = Math.min(warpSpeed, ((warpDistance + 1) * accelRate * decelRate) / (accelRate + decelRate));

    const accelDuration = Math.log(warpSpeed / accelRate) / accelRate;
    const cruiseDuration = warpDistance / warpSpeed - 1.0 / accelRate - 1.0 / decelRate + 1.0 / warpSpeed;
    const decelDuration = Math.log(warpSpeed / decelRate) / decelRate;
    const accelDistance = warpSpeed / accelRate;
    const cruiseDistance = warpSpeed * cruiseDuration;
    const decelDistance = warpSpeed / decelRate - 1;
    return { accelDuration, cruiseDuration, decelDuration, accelDistance, cruiseDistance, decelDistance, accelRate, decelRate, warpSpeed };
  }

  /** Closed-form warp trajectory. Mutates p and v; may drop the ball out of warp. */
  warpDistance(ball: Ball, p: Vec3, v: Vec3, t: number, interpolating: boolean): number {
    const warpFactor = ball.ownerId;
    const warpLen = ball.lastCollision;
    const c = this.setupWarpConstants(warpFactor, warpLen);

    let speed: number;
    let distance: number;
    const dir = ball.goto.clone().sub(p).normalize();

    if (t < c.accelDuration) {
      speed = c.accelRate * Math.exp(c.accelRate * t);
      const currSpeed = v.length();
      if (currSpeed > speed) speed = currSpeed; // keep speed continuous entering warp
      v.copy(dir.clone().scale(speed));
      distance = Math.exp(c.accelRate * t);
      p.copy(ball.goto.clone().sub(dir.clone().scale(c.accelDistance + c.cruiseDistance + c.decelDistance - distance)));
    } else if (t - c.accelDuration < c.cruiseDuration) {
      speed = c.warpSpeed;
      v.copy(dir.clone().scale(speed));
      distance = c.warpSpeed * (t - c.accelDuration) + c.accelDistance;
      p.copy(ball.goto.clone().sub(dir.clone().scale(c.accelDistance + c.cruiseDistance + c.decelDistance - distance)));
    } else if (t - c.cruiseDuration - c.accelDuration < c.decelDuration + 1) {
      const td = t - c.cruiseDuration - c.accelDuration;
      speed = c.warpSpeed * Math.exp(-c.decelRate * td);
      v.copy(dir.clone().scale(speed));
      distance = c.warpSpeed / c.decelRate - (c.warpSpeed / c.decelRate) * Math.exp(-c.decelRate * td) + c.accelDistance + c.cruiseDistance;
      p.copy(ball.goto.clone().sub(dir.clone().scale(c.accelDistance + c.cruiseDistance + c.decelDistance - distance)));

      // Drop out of warp once slow enough
      if (speed < Math.min(ball.maxVel / 2.0, 100.0) && !interpolating) {
        this.emit({ type: 'deactivatingWarp', ballId: ball.id, stamp: this.currentTime });
        ball.isMassive = true;
        this.stop(ball);
      }
    } else {
      // Warp overran (shouldn't happen); land at the exit point
      p.copy(ball.goto);
      v.set(0, 0, 0);
      distance = 0;
      if (!interpolating) {
        this.emit({ type: 'deactivatingWarp', ballId: ball.id, stamp: this.currentTime });
        ball.isMassive = true;
        this.stop(ball);
      }
    }
    return distance;
  }

  // ------------------------------------------------------------------
  // Orientation (yaw/pitch/roll, the EVE-default non-dynamical path)
  // ------------------------------------------------------------------

  private calculateYawPitchRoll(ball: Ball, snap = false): void {
    ball.oldPitch = ball.newPitch;
    ball.oldYaw = ball.newYaw;
    ball.oldRoll = ball.newRoll;
    ball.oldRollSpeed = ball.newRollSpeed;

    let massModifier = ball.mass / this.friction;
    massModifier = massModifier !== 0.0 ? 100.0 / massModifier : 100.0;

    let direction: Vec3;
    const vel2 = ball.newVel.lengthSq();
    if (vel2 === 0.0) {
      direction = ball.goto.clone().sub(ball.newPos);
    } else {
      if (ball.isWarpish()) {
        if (ball.isAligningForWarp()) {
          direction = ball.collisions.length === 0 ? ball.goto.clone().sub(ball.newPos) : ball.newVel.clone();
        } else {
          direction = ball.newVel.clone();
          if (massModifier < 1.0 && this.currentTime !== ball.effectStamp) {
            massModifier = Math.min(1.0, massModifier * Math.min(15.0, (this.currentTime - ball.effectStamp) * 2));
          }
        }
      } else {
        direction = ball.newVel.clone();
      }

      if (ball.mode !== BallMode.MISSILE && !snap) {
        const offset = ball.goto.clone().sub(ball.newPos).normalize();
        direction = direction.add(offset.scale(Math.sqrt(vel2) * 0.3));
      }
    }

    ball.newYaw = Math.atan2(direction.x, direction.z);
    const r = direction.length();
    ball.newPitch = r > 0.0 ? -Math.asin(Math.min(1.0, Math.max(-1.0, direction.y / r))) : 0.0;

    const yawPair = { a: ball.oldYaw, b: ball.newYaw };
    modulo2pi(yawPair);
    ball.oldYaw = yawPair.a;
    ball.newYaw = yawPair.b;
    const pitchPair = { a: ball.oldPitch, b: ball.newPitch };
    modulo2pi(pitchPair);
    ball.oldPitch = pitchPair.a;
    ball.newPitch = pitchPair.b;

    if (snap) {
      ball.newRoll = 0.0;
      ball.oldRoll = 0.0;
      ball.newRollSpeed = 0.0;
      ball.oldRollSpeed = 0.0;
      ball.yawDelta = 0.0;
      return;
    }

    const tmp = 1.0 / (1.0 + massModifier * 0.5);
    ball.newYaw = (ball.oldYaw + massModifier * 0.5 * ball.newYaw) * tmp;
    ball.newPitch = (ball.oldPitch + massModifier * 0.5 * ball.newPitch) * tmp;

    ball.yawDelta = Math.asin(Math.sin(ball.newYaw - ball.oldYaw));
    ball.newRollSpeed += ball.yawDelta * this.rollSpeedAcceleration;
    ball.newRollSpeed += ball.oldRollSpeed * this.rollSpeedDecay * this.dt;
    ball.newRollSpeed = Math.max(-HALF_PI, Math.min(HALF_PI, ball.newRollSpeed));
    ball.newRoll += ball.newRollSpeed * this.rollAcceleration * this.dt;
    ball.newRoll += ball.oldRoll * this.rollDecay * this.dt;
    ball.newRoll = Math.max(-HALF_PI, Math.min(HALF_PI, ball.newRoll));
  }

  // ------------------------------------------------------------------
  // Evolve: one tick of the whole park
  // ------------------------------------------------------------------

  evolve(timestamp = 0): void {
    const ids = [...this.freeBalls.keys()].sort((a, b) => a - b);

    // Phase 1: behavior accelerations
    for (const id of ids) {
      const ball = this.freeBalls.get(id);
      if (!ball || ball.isMoribund || ball.inDeadBubble) continue;
      this.evolveBehaviorForBall(ball);
    }

    // Phase 2: integration (into the old-slots; swapped in phase 3)
    for (const id of ids) {
      const ball = this.freeBalls.get(id);
      if (!ball || ball.isMoribund || ball.inDeadBubble) continue;

      const p = ball.newPos.clone();
      const v = ball.newVel.clone();
      const accel = ball.lastG.clone().add(ball.lastC);

      if (ball.isWarping()) {
        this.warpDistance(ball, p, v, (this.currentTime - ball.effectStamp) * this.dt, false);
        if (ball.mode === BallMode.STOP) {
          // Dropped out of warp: continue integrating from the exit point
          ball.newPos.copy(p);
          ball.newVel.copy(v);
          this.integrate(p, v, accel, ball.mass * ball.agility, this.friction, ball.timeFactor, this.dt);
        }
      } else {
        this.integrate(p, v, accel, ball.mass * ball.agility, this.friction, ball.timeFactor, this.dt);
      }
      ball.oldPos.copy(p);
      ball.oldVel.copy(v);
    }

    // Phase 3: publish (swap buffers), orientation, timestamps, missile events
    for (const id of ids) {
      const ball = this.freeBalls.get(id);
      if (!ball || ball.isMoribund) continue;
      if (ball.inDeadBubble) {
        ball.inDeadBubble = false;
        continue;
      }

      if (timestamp !== 0) {
        ball.oldTime = ball.newTime === 0 ? timestamp - this.tickInterval : ball.newTime;
        ball.newTime = timestamp;
      }

      // Swap old and new values for position and velocity
      const tp = ball.newPos;
      ball.newPos = ball.oldPos;
      ball.oldPos = tp;
      const tv = ball.newVel;
      ball.newVel = ball.oldVel;
      ball.oldVel = tv;

      this.calculateYawPitchRoll(ball);

      if (ball.mode === BallMode.MISSILE && this.isMaster) {
        const owner = this.balls.get(Math.abs(ball.ownerId));
        if (!owner || owner.isCloaked) {
          ball.collisions.push(ball.id); // self-collision triggers detonation
        }
        for (const cid of ball.collisions) {
          this.emit({ type: 'collision', ballId: ball.id, otherId: cid, stamp: this.currentTime });
        }
      }
    }

    this.currentTime++;
    if (timestamp !== 0) this.time = timestamp;
  }
}
